import { Box, Skeleton, useTheme } from "@mui/material";
import * as React from "react";

import { EventRecord } from "@/shared/domain/events";
import { ErrorOutlineIcon } from "@/shared/foundation/icons/mui";

import { EventNameForm } from "../../events";
import { RobotoTypography } from "../../RobotoTypography";
import {
  useWorkspaceEvent,
  useUpdateWorkspaceEvent,
} from "../WorkspaceCtx/useWorkspaceEvents";

import styles from "./EventName.module.css";

interface EventNameProps {
  eventId: EventRecord["event_id"];
}

export function EventName({ eventId }: EventNameProps) {
  const eventQuery = useWorkspaceEvent(eventId);
  const updateEventMutation = useUpdateWorkspaceEvent();
  const theme = useTheme();

  const [isEditing, setIsEditing] = React.useState<boolean>(false);

  if (eventQuery.isPending) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Name
        </RobotoTypography>
        <Skeleton height="24px" variant="rounded" />
      </section>
    );
  }

  if (eventQuery.isError) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Name
        </RobotoTypography>
        <div className={styles.errorContainer}>
          <ErrorOutlineIcon color="error" />
          <RobotoTypography>Failed to load event name</RobotoTypography>
        </div>
      </section>
    );
  }

  const { name } = eventQuery.data;

  if (isEditing) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Name
        </RobotoTypography>
        <div>
          <EventNameForm
            name={name}
            onSubmit={async (name) => {
              await updateEventMutation.mutateAsync({
                eventId,
                request: {
                  name,
                },
              });
              setIsEditing(false);
            }}
            onCancel={() => setIsEditing(false)}
            inputClassName={styles.name}
          />
        </div>
      </section>
    );
  }

  return (
    <section>
      <RobotoTypography component={"h3"} className={styles.title}>
        Name
      </RobotoTypography>
      <Box
        onClick={() => setIsEditing(true)}
        sx={{
          ":hover": {
            backgroundColor: theme.palette.hoverBackground.main,
            borderRadius: "0.25rem",
            cursor: "pointer",
          },
        }}
      >
        <RobotoTypography component="p" className={styles.name}>
          {name}
        </RobotoTypography>
      </Box>
    </section>
  );
}
